import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import {
	View,
	TouchableOpacity,
	StyleSheet,
	FlatList,
	useWindowDimensions,
	Image,
	Pressable,
	DeviceEventEmitter,
} from 'react-native'
import Animated, {
	useSharedValue,
	useAnimatedStyle,
	withTiming,
	Easing,
	FadeIn,
	FadeOut,
	interpolate,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ThemeConsumer } from '@rneui/themed';
import { DrawerActions } from '@react-navigation/native';
import Popover from 'react-native-popover-view';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import { ZenDivider, ZenIcon, ZenText } from 'zen-core-elements';

import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { pageDirty, setSidebarExpanded } from '../redux/slices/commonSlice';

import { appColors, fontSize } from '../themes/appTheme';
import CommonDialogBox from '../shared/commonComponents/commonDialogBox';
import LocationModal from '../components/newSellscreen/sellscreenProducts/locationModal';

import type { SharedValue } from 'react-native-reanimated';
import { IconType } from 'zen-core-elements';
import { DrawerContentComponentProps, useDrawerStatus } from '@react-navigation/drawer';
import { DrawerScreens, useAppNavigation } from '../types/@react-navigation/navigation';
import { config } from '../shared/environment';

type SubMenuItem = {
	menuName: string;
	navigation: string;
	isRead?: boolean;
	isWrite?: boolean;
};

type MenuItem = {
	menuName: string;
	navigation: string;
	iconName: string;
	iconType?: IconType;
	iconSize?: number;
	subMenu?: SubMenuItem[];
	isRead?: boolean;
	isWrite?: boolean;
};
export type DrawerContent = {
	state: { routes: [{ key: string, name: string }], history: [{ key: string }], index: number }
	filteredMenuList: MenuItem[],
	myAccountStackPermission: { isRead: boolean, isWrite: boolean },
	intitalRouteName: string
}

const menuList: MenuItem[] = [
	{ menuName: 'Dashboard', navigation: 'DashboardStack', iconName: 'dashboard', iconType: 'material', iconSize: 23, subMenu: [] },
	{ menuName: 'Sell Screen', navigation: 'SellScreenStack', iconName: 'cart-outline', iconType: 'material-community', iconSize: 23, subMenu: [] },
	{
		menuName: 'Products', navigation: 'ProductStack', iconName: 'shopping-outline', iconType: 'material-community', iconSize: 23, subMenu: [
			{ menuName: 'All Products', navigation: 'ProductStack' },
			{ menuName: 'Categories', navigation: 'CategoryStack' },
			{ menuName: "Brands", navigation: 'BrandStack' },
			{ menuName: "Inventory", navigation: 'InventoryStack' },
			{ menuName: "Options", navigation: 'OptionStack' },
			{ menuName: "Modifiers", navigation: 'ModifierScreen' },
			{ menuName: "Image Gallery", navigation: 'ImageGalleryStack' },
		]
	},
	{ menuName: 'orders', navigation: 'OrdersStack', iconName: 'receipt-outline', iconType: 'ionicon', iconSize: 23, subMenu: [] },

	{
		menuName: 'User Management', navigation: 'CustomerStack', iconName: 'account-outline', iconType: 'material-community', iconSize: 30, subMenu: [
			{ menuName: 'Customers', navigation: 'CustomerStack' },
			{ menuName: 'Customer Groups', navigation: 'CustomerGroupStack' },
			{ menuName: "Staff", navigation: 'StaffStack' },
			{ menuName: "Invited Staff", navigation: 'InvitedStaff' },
			{ menuName: "Roles", navigation: 'RoleStack' },
		]
	},

	{
		menuName: 'Discount', navigation: 'ManualDiscountStack', iconName: 'tag-text-outline', iconType: 'material-community', iconSize: 23, subMenu: [
			{ menuName: 'Manual Discounts', navigation: 'ManualDiscountStack' },
			{ menuName: 'Automatic Discounts', navigation: 'AutomaticDiscountStack' },
			{ menuName: 'Buy X Get Y Discounts', navigation: 'BuyXgetYDiscountStack' },
			{ menuName: 'Pay X Get Free', navigation: 'PayXGetFreeStack' },
		]
	},
	{ menuName: 'Taxes', navigation: 'TaxStack', iconName: 'currency-usd', iconType: 'material-community', iconSize: 25, subMenu: [] },
	{ menuName: 'Settings', navigation: 'SettingStack', iconName: 'cog-outline', iconType: 'material-community', iconSize: 23, subMenu: [] },

];


export const EXPANDED_WIDTH = 280;
const navigationMap: Record<string, string> = {};
export const COLLAPSED_WIDTH = 80;
const SUBMENU_ITEM_HEIGHT = 40;

// Initialize navigationMap immediately
menuList?.forEach((list) => {
	if (list?.subMenu?.length) {
		list?.subMenu?.forEach((subList) => {
			navigationMap[subList?.navigation] = list.navigation
		})
	}
	else {
		navigationMap[list?.navigation] = list.navigation
	}
})

const SidebarMenuItem = React.memo(({
	item,
	index,
	sidebarOpenSV,
	isActiveSubmenu,
	onItemPress,
	onToggleSubmenu,
	currentPage
}: {
	item: MenuItem;
	index: number;
	sidebarOpenSV: SharedValue<number>;
	isActiveSubmenu: boolean;
	onItemPress: (index: number, subIndex?: number) => void;
	onToggleSubmenu: (index: number) => void;
	currentPage: string
}) => {

	const rotateSV = useSharedValue(isActiveSubmenu ? 180 : 0);

	useEffect(() => {
		rotateSV.value = withTiming(isActiveSubmenu ? 180 : 0, { duration: 120, easing: Easing.out(Easing.cubic) });
	}, [isActiveSubmenu]);

	const chevronStyle = useAnimatedStyle(() => ({
		transform: [
			{
				rotate: `${rotateSV.value}deg`,
			},
		],
		opacity: interpolate(sidebarOpenSV.value, [0, 1], [0, 1]),
	}));


	const labelStyle = useAnimatedStyle(() => ({
		opacity: sidebarOpenSV.value,
		transform: [
			{ translateX: interpolate(sidebarOpenSV.value, [0, 1], [-8, 0]) },
		],
	}));

	const handlePress = useCallback(() => {
		if (item.subMenu && item.subMenu.length > 0) {
			onItemPress(index);
			onToggleSubmenu(index);
		} else {
			onItemPress(index);
		}
	}, [item.subMenu, index, onItemPress, onToggleSubmenu]);

	const handleSubMenuPress = useCallback((i: number) => {
		onItemPress(index, i);
	}, [index, onItemPress]);

	return (
		<ThemeConsumer>
			{({ theme }) => (
				<Animated.View>
					<TouchableOpacity
						style={[styles.menuRow, { backgroundColor: navigationMap[currentPage] == item.navigation ? theme?.lightPrimary : theme?.light, }]}
						onPress={handlePress}
					>
						<View style={styles.iconWrap}>
							<ZenIcon type={item?.iconType!!} name={item.iconName} size={item.iconSize} />
						</View>

						<Animated.View style={[styles.labelWrap, labelStyle]} pointerEvents={'auto'}>
							<ZenText content={item?.menuName} numberOfLines={1} bold style={styles.menuText} />
						</Animated.View>

						{item.subMenu && item.subMenu.length > 0 ? (
							<Animated.View style={[styles.chevronWrap, chevronStyle]}>
								<MaterialCommunityIcons name="chevron-down" size={20} />
							</Animated.View>
						) : null}
					</TouchableOpacity>

					{
						item.subMenu && item.subMenu.length > 0 ? (
							isActiveSubmenu ? (
								<Animated.View
									style={styles.subMenuOuter}
								>
									{item.subMenu.map((s, i) => (
										<Animated.View
											key={s.menuName}
											style={styles.subMenuRow}
										>
											<TouchableOpacity style={styles.subMenuTouchable} onPress={() => handleSubMenuPress(i)}>
												{currentPage == s.navigation && <MaterialCommunityIcons name={"check"} size={16} color={theme?.primary} style={{ position: "absolute", left: -40 }} />}
												<ZenText content={s?.menuName} numberOfLines={1} bold={currentPage == s.navigation} style={styles.subMenuText} />
											</TouchableOpacity>
										</Animated.View>
									))}
								</Animated.View>
							) : null
						) : null
					}
				</Animated.View >
			)}
		</ThemeConsumer>
	);
});

export default function CustomDrawer(drawerContent: DrawerContentComponentProps & DrawerContent) {
	const scrollRef = useRef<FlatList>(null)
	const { width: mobileWidth } = useWindowDimensions();

	const insets = useSafeAreaInsets()

	const dispatch = useAppDispatch()
	//Variable to store ispageDirty value
	const isPageDirty = useAppSelector((state) => state?.common?.isPageDirty)
	const navigation = useAppNavigation()
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [openSubmenuIndex, setOpenSubmenuIndex] = useState<number | null>(null);
	const [currentPage, setCurrentPage] = useState(drawerContent?.intitalRouteName)
	const [selectLocationModal, setSelectLocationModal] = useState(false);
	const currentLocation = useAppSelector((state) => state?.common?.currentLocation);
	const width = useSharedValue(COLLAPSED_WIDTH);
	const sidebarOpenSV = useSharedValue(0);
	//Variable to handle leave Popover
	const [leavePopover, setLeavePopover] = useState<{ visible: boolean, page: keyof DrawerScreens | string }>({ visible: false, page: "" })

	const confirmation = (item: { id: number }) => {
		setLeavePopover((prev) => ({ ...prev, visible: false }))
		if (item?.id == 2) {
			navigation.navigate(leavePopover?.page as never)
			drawerContent?.navigation?.openDrawer()
			dispatch(pageDirty(false))
		}
	}

	useEffect(() => {
		let routeName = drawerContent?.intitalRouteName
		if (drawerContent?.state?.history?.length > 0) {
			let subContent = drawerContent?.state?.history?.filter((item) => item.type !== "drawer")
			routeName = subContent?.[subContent?.length - 1].key.split("-")[0]
		}
		setCurrentPage(routeName)
	}, [drawerContent?.state?.index])
	const isLargeScreen = mobileWidth >= 900;

	const drawerStatus = useDrawerStatus(); // 'open' | 'closed' — tracks the front drawer in portrait

	useEffect(() => {
		if (sidebarOpen) {
			sliderOpen();
		} else {
			sliderClose();
			// setOpenSubmenuIndex(null);
		}
	}, [isLargeScreen]);

	// Keep sidebarOpen accurate when the user opens/closes the front drawer manually
	// in portrait (swipe gesture, hamburger icon, hardware back button, etc.)
	useEffect(() => {
		if (isLargeScreen) return; // only relevant to the front drawer in portrait
		const isOpen = drawerStatus === 'open';
		if (isOpen !== sidebarOpen) {
			setSidebarOpen(isOpen);
			sidebarOpenSV.value = withTiming(isOpen ? 1 : 0, { duration: 200, easing: Easing.out(Easing.cubic) });
			dispatch(setSidebarExpanded(isOpen));
			if (!isOpen) setOpenSubmenuIndex(null);
		}
	}, [drawerStatus, isLargeScreen, sidebarOpen]);

	useEffect(() => {
		if (!sidebarOpen) return; // only matters while the drawer is actually visible/expanded
		const currentParentIndex = drawerContent?.filteredMenuList?.findIndex((item) =>
			item?.navigation === navigationMap[currentPage] ||
			item?.subMenu?.some((s) => s?.navigation === currentPage)
		);

		if (currentParentIndex != null && currentParentIndex >= 0 && !isLargeScreen) {
			const parent = drawerContent?.filteredMenuList?.[currentParentIndex];
			if (parent?.subMenu && parent.subMenu.length > 0) {
				setOpenSubmenuIndex(currentParentIndex);
			}
		}
	}, [currentPage, sidebarOpen, drawerContent?.filteredMenuList]);

	const sliderOpen = () => {
		dispatch(setSidebarExpanded(true));
		setSidebarOpen(true)
		width.value = withTiming(EXPANDED_WIDTH, { duration: 300, easing: Easing.out(Easing.cubic) });
		sidebarOpenSV.value = withTiming(1, { duration: 300, easing: Easing.out(Easing.cubic) });
		drawerContent?.navigation?.dispatch?.(DrawerActions.openDrawer());
	}
	const sliderClose = () => {
		sidebarOpenSV.value = withTiming(0, { duration: 300, easing: Easing.out(Easing.quad) });
		width.value = withTiming(COLLAPSED_WIDTH, { duration: 300, easing: Easing.out(Easing.quad) });
		setSidebarOpen(false)
		setTimeout(() => {
			dispatch(setSidebarExpanded(false)); // Shrink wrapper to 80px after animation finishes
		}, 300);

		drawerContent?.navigation?.dispatch?.(DrawerActions.closeDrawer());
	}
	const containerStyle = useAnimatedStyle(() => {
		const animatedWidth = interpolate(
			sidebarOpenSV.value,
			[0, 1],
			[COLLAPSED_WIDTH, EXPANDED_WIDTH]
		);
		return { width: animatedWidth };
	});

	useEffect(() => {
		const subscription = DeviceEventEmitter.addListener('DRAWER_CLOSE', () => {
			sliderClose();
		})
		return () => subscription.remove()
	}, [drawerContent?.navigation])

	const toggleSidebar = useCallback(() => {
		if (mobileWidth >= 900) {
			if (sidebarOpenSV?.value) {
				setOpenSubmenuIndex(null);
				sliderClose();
			} else {
				sliderOpen()
			}
		}
		else {
			navigation.dispatch(DrawerActions.toggleDrawer())
		}
	}, [mobileWidth, currentPage]);

	// const checkNavigation = (route: string) => {
	// 	if (!isPageDirty) {
	// 		navigation.navigate(route as never)
	// 		toggleSidebar()
	// 	} else {
	// 		setLeavePopover({ visible: true, page: route })
	// 	}
	// }

	const STACK_SCREENS = [
		{ route: "SellScreenStack", screen: "NewSellScreen" },
		{ route: "ProductStack", screen: "ProductList" },
		{ route: "OrdersStack", screen: "OrderList" },
		{ route: "CustomerStack", screen: "CustomerScreen" },
		{ route: "DashboardStack", screen: "dashboard" },
		{ route: "ManualDiscountStack", screen: "ManualDiscount" },
		{ route: "PayXGetFreeStack", screen: "PayXGetFree" },
		{ route: "BuyXgetYDiscountStack", screen: "BuyXgetYDiscount" },
		{ route: "AutomaticDiscountStack", screen: "AutomaticDiscount" },
		,
		{ route: "ZoneStack", screen: "ZoneList" },
		{ route: "CategoryStack", screen: "CategoryList" },
		{ route: "ProfileStack", screen: "AccountSettings" },
		{ route: "SettingStack", screen: "SettingScreen" }
		// add your stack screens here
	];


	const checkNavigation = (route: string, isRead?: boolean, isWrite?: boolean) => {
		if (isPageDirty) {
			setLeavePopover({ visible: true, page: route });
			return;
		}
		const stackRoute = STACK_SCREENS.find((item) => item?.route == route)

		if (stackRoute) {
			navigation.navigate(
				stackRoute.route as any,
				{
					screen: stackRoute.screen,
					params: { isRead, isWrite }
				}
			);

		} else {
			navigation.navigate(route as any, { isRead, isWrite } as never);
		}

		if (mobileWidth >= 900) {
			// Defer the drawer update to allow navigation to start smoothly
			setTimeout(() => {
				if (sidebarOpenSV?.value) {
					setOpenSubmenuIndex(null);
					sliderClose();
				}
				// Removed the 'else { sliderOpen() }' block to prevent unwanted expansion
			}, 300);
		}
	};


	const handleMenuPress = useCallback((index: number, subIndex?: number) => {
		// Removed eager sliderOpen() here. We only auto-open for submenus.
		scrollRef?.current?.scrollToIndex({ index, animated: true })
		const item = drawerContent?.filteredMenuList[index];

		if (!item.subMenu || item.subMenu.length === 0) {
			checkNavigation(item?.navigation, item?.isRead, item?.isWrite)
			setOpenSubmenuIndex(null);
		}
		else {
			// If clicking a parent with submenu, ensure it's open
			if (!sidebarOpenSV?.value) {
				sliderOpen();
			}
			const subMenuItem = item?.subMenu[subIndex!!]
			const subMenuNavigation = subMenuItem?.navigation
			if (subMenuNavigation)
				checkNavigation(subMenuNavigation, subMenuItem?.isRead, subMenuItem?.isWrite)
		}
	}, [isPageDirty, drawerContent?.filteredMenuList]);

	const handleToggleSubmenu = useCallback((index: number) => {
		if (!sidebarOpenSV?.value) sliderOpen();
		setOpenSubmenuIndex((prev) => (prev === index ? null : index));
	}, []);

	const headerAnimation = useAnimatedStyle(() => ({
		opacity: sidebarOpenSV.value,
		transform: [
			{ translateX: interpolate(sidebarOpenSV.value, [0, 1], [-8, 0]) },
		],
	}));

	const overlayAnimatedStyle = useAnimatedStyle(() => {
		return {
			left: width.value, // now allowed
		};
	});
	const currentUser = useAppSelector((state) => state?.common?.currentUser?.value);
	return (
		<ThemeConsumer>
			{({ theme }) => (
				<View style={{ flex: 1, zIndex: 100 }} pointerEvents="box-none">
					{isLargeScreen && sidebarOpen ? <Animated.View
						entering={FadeIn.duration(200)}
						exiting={FadeOut.duration(100)}
						style={[{
							position: "absolute",
							backgroundColor: "rgba(0, 0, 0, 0.5)",
							width: mobileWidth,
							height: "100%",
							zIndex: 0,
						}, overlayAnimatedStyle]}
						pointerEvents="auto"
					>
						<TouchableOpacity style={{ flex: 1 }}
							onPress={() => { toggleSidebar() }}>
						</TouchableOpacity>
					</Animated.View> : undefined}
					<Animated.View
						pointerEvents="auto"
						style={[
							styles.container,
							containerStyle,
						]}
					>
						<View style={[styles.header, { overflow: "hidden", paddingTop: insets?.top }]}>
							<TouchableOpacity onPress={toggleSidebar} style={styles.collapseBtn}>
								<Image source={{ uri: "https://bloggallery.s3.us-west-2.amazonaws.com/Mobile/306669425_522650896529494_3025486609798314771_n-removebg-preview_1708063112621.png" }} style={{ width: mobileWidth > 600 ? 65 : 40, height: mobileWidth > 600 ? 65 : 40 }} />
								<Animated.View style={headerAnimation}>
									<ZenText content={"POS"} size={theme.mediumFontSize} bold color={theme?.dark} />
								</Animated.View>
							</TouchableOpacity>
							<Animated.View style={headerAnimation}>
								<ZenIcon name={"close"} type={"material-community"} size={22} color={theme?.grey} onPress={toggleSidebar} />
							</Animated.View>
						</View>
						<View style={{ marginVertical: 6, alignItems: 'center', width: '100%' }}>
							{sidebarOpen && (
								<Animated.View style={[headerAnimation, { width: '100%', paddingHorizontal: 15 }]}>
									<TouchableOpacity
										style={[styles.locationButton, { backgroundColor: theme?.lightPrimary, }]}
										onPress={() => setSelectLocationModal(true)}
									>
										<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
											<ZenIcon name="map-marker-outline" type="material-community" size={23} color={theme?.primary} />
											<ZenText
												content={currentLocation?.name || 'Select Location'}
												size={theme.smallerFontSize}
												color={theme?.dark}
												numberOfLines={1}
												style={{ flex: 1 }}
											/>
										</View>
									</TouchableOpacity>
								</Animated.View>
							)}
						</View>
						<FlatList
							ref={scrollRef}
							style={{ flex: 1 }}
							data={drawerContent?.filteredMenuList}
							keyExtractor={(i) => i.menuName}
							renderItem={({ item, index }) => (
								<SidebarMenuItem
									item={item}
									index={index}
									sidebarOpenSV={sidebarOpenSV}
									isActiveSubmenu={openSubmenuIndex === index}
									onItemPress={handleMenuPress}
									onToggleSubmenu={handleToggleSubmenu}
									currentPage={currentPage}
								/>
							)}
							/*
							 * handleMenuPress calls scrollToIndex on every menu tap.
							 * VirtualizedList throws an Invariant Violation for any index
							 * past the highest measured frame unless one of getItemLayout
							 * or onScrollToIndexFailed is supplied - and with
							 * removeClippedSubviews the lower menu entries are unmeasured
							 * until they scroll into view, so tapping one further down the
							 * drawer crashes the press handler.
							 */
							onScrollToIndexFailed={(info) => {
								scrollRef?.current?.scrollToOffset({
									offset: info.averageItemLength * info.index,
									animated: true,
								});
							}}
							showsVerticalScrollIndicator={false}
							contentContainerStyle={{ paddingVertical: 8 }}
							windowSize={5}
							maxToRenderPerBatch={10}
							initialNumToRender={15}
							removeClippedSubviews={true}
						/>
						<ZenDivider />
						<View style={{ paddingHorizontal: 15, marginTop: 10, flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
							{drawerContent?.myAccountStackPermission?.isRead ? <TouchableOpacity onPress={() => checkNavigation('ProfileStack')} style={[styles.collapseBtn]}>
								<Image source={{ uri: currentUser?.profileImage ? config.AWSImageUrl + currentUser?.profileImage : "https://as2.ftcdn.net/v2/jpg/03/64/21/11/1000_F_364211147_1qgLVxv1Tcq0Ohz3FawUfrtONzz8nq3e.jpg" }}
									style={{ width: mobileWidth > 600 ? 50 : 30, height: mobileWidth > 600 ? 50 : 30, borderRadius: 40, marginRight: 10 }}
								/>
								{sidebarOpen && <Animated.View style={headerAnimation}>
									<ZenText content={"Hai!"} size={theme.smallerFontSize} color={theme?.grey} style={{ textAlign: "left" }} />
									<ZenText content={currentUser?.name} size={theme.mediumFontSize} bold color={theme?.primary} style={{ textAlign: "center" }} />
								</Animated.View>}
							</TouchableOpacity> : undefined}
							{sidebarOpen && drawerContent?.myAccountStackPermission?.isRead && <Animated.View style={[headerAnimation, styles.footer]}>
								<TouchableOpacity onPress={() => { checkNavigation('ProfileStack', drawerContent?.myAccountStackPermission?.isRead, drawerContent?.myAccountStackPermission?.isWrite) }}>
									<ZenIcon name={"chevron-right"} type={"material-community"} size={22} color={theme?.primary} style={{ justifyContent: "flex-end" }} />
								</TouchableOpacity>
							</Animated.View>}
						</View>
					</Animated.View>
					{/* For leave confirmation */}
					<Popover isVisible={leavePopover?.visible} popoverStyle={{ width: mobileWidth > 600 ? 400 : 300, borderRadius: 10 }}>
						<CommonDialogBox type='EDIT' replaceValue='page' confirmation={confirmation} />
					</Popover>
					<LocationModal
						isModalOpen={selectLocationModal}
						setModalOpen={setSelectLocationModal}
						showTrigger={false}
					/>
				</View>
			)}
		</ThemeConsumer>
	);
}

const styles = StyleSheet.create({
	safe: { flex: 1, backgroundColor: '#f2f2f7' },
	container: {
		backgroundColor: '#fff',
		height: '100%',
		paddingVertical: 12,
		borderRightWidth: StyleSheet.hairlineWidth,
		borderRightColor: '#ddd',
		shadowColor: '#000',
		shadowOpacity: 0.04,
		shadowRadius: 8,
		elevation: 4,
	},
	header: {
		paddingHorizontal: 8,
		marginBottom: 8,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	collapseBtn: {
		height: 48,
		flexDirection: "row",
		alignItems: "center",
		gap: 10
	},
	locationButton: {
		flexDirection: 'row',
		alignItems: 'center',
		borderRadius: 10,
		paddingVertical: 10,
		paddingHorizontal: 12,
		height: 48,
	},
	collapsedLocationBtn: {
		width: 48,
		height: 48,
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: 10,
	},
	menuRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 10,
		marginHorizontal: 15,
		borderRadius: 10
	},
	iconWrap: {
		width: 48,
		alignItems: 'center',
		justifyContent: 'center',
	},
	labelWrap: {
		flex: 1,
		justifyContent: 'center',
		paddingLeft: 8,
	},
	menuText: {
		fontSize: fontSize?.smallerFontSize
	},
	chevronWrap: {
		width: 36,
		alignItems: 'center',
		justifyContent: 'center',
	},
	subMenuOuter: {
		paddingLeft: 72,
		paddingVertical: 4
	},
	subMenuRow: {
		height: SUBMENU_ITEM_HEIGHT,
		justifyContent: 'center',
		borderRadius: 8,
		paddingRight: 8,
	},
	subMenuTouchable: {
		flexDirection: "row",
		alignItems: "center",
		gap: 10,
		paddingVertical: 8,
	},
	subMenuText: {
		fontSize: fontSize?.smallFontSize,
	},
	footer: {
		backgroundColor: appColors?.lightPrimary,
		elevation: 2,
		borderRadius: 10,
		padding: 5,
		marginRight: 15
	},
});